import React, { useEffect, useMemo, useState } from "react";
import { createPluginRegistration } from "@embedpdf/core";
import { EmbedPDF } from "@embedpdf/core/react";
import { usePdfiumEngine } from "@embedpdf/engines/react";
import { DocumentContent, DocumentManagerPluginPackage } from "@embedpdf/plugin-document-manager/react";
import { Viewport, ViewportPluginPackage } from "@embedpdf/plugin-viewport/react";
import { Scroller, ScrollPluginPackage, useScroll } from "@embedpdf/plugin-scroll/react";
import { RenderLayer, RenderPluginPackage } from "@embedpdf/plugin-render/react";
import { AnnotationLayer, AnnotationPluginPackage, useAnnotation, LockModeType } from "@embedpdf/plugin-annotation/react";
import { PagePointerProvider, InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager/react";
import { SelectionLayer, SelectionPluginPackage } from "@embedpdf/plugin-selection/react";
import { HistoryPluginPackage } from "@embedpdf/plugin-history/react";
import { FormPluginPackage } from "@embedpdf/plugin-form/react";
import { ThumbnailsPane, ThumbImg, ThumbnailPluginPackage } from "@embedpdf/plugin-thumbnail/react";
import styled from "styled-components";

const StyledPreviewContainer = styled.div({
    width: "100%",
    height: "600px",
    overflow: "hidden",
    display: "flex",
});

const StyledThumbnailSidebar = styled.div({
    width: "120px",
    height: "100%",
    flexShrink: 0,
    overflow: "hidden",
    borderRight: "1px solid #dfe3e6",
    background: "#f7f7f7",
});

const StyledThumbnailItem = styled.div({
    position: "absolute",
    left: "0",
    right: "0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    padding: "5px",
    boxSizing: "border-box",
});

const StyledThumbnailImage = styled.div({
    overflow: "hidden",
});

const StyledThumbnailPageNumber = styled.div({
    fontSize: "12px",
    marginTop: "4px",
});

const StyledDocumentContainer = styled.div({
    flex: 1,
    minWidth: 0,
    height: "100%",
    overflow: "hidden",
});

const StyledFieldHighlightContainer = styled.div({
    position: "absolute",
    inset: "0",
    pointerEvents: "none",
    border: "2px solid rgba(128, 78, 146, 0.45)",
    background: "rgba(128, 78, 146, 0.08)",
    borderRadius: "3px",
    boxSizing: "border-box",
});

/**
 * Keeps the Annotation Plugin in form/fill mode.
 *
 * This prevents form widgets from being dragged, resized or repositioned,
 * while still allowing the user to interact with the fields.
 */
const FormFillMode = ({ documentId }) => {
    const { provides: annotationApi } = useAnnotation(documentId);

    useEffect(() => {
        if (!annotationApi) {
            return;
        }

        annotationApi.setLocked({
            type: LockModeType.Include,
            categories: ["form"],
        });
    }, [annotationApi]);

    return null;
};

/**
 * Adds a visual highlight over the PDF form fields.
 *
 * The actual form field remains underneath this layer and continues to
 * receive interaction because the highlight uses pointerEvents: "none".
 */
const FormFieldHighlightLayer = ({
    documentId,
    pageIndex,
}) => {
    const [fields, setFields] = useState([]);

    useEffect(() => {
        setFields([]);
    }, [documentId, pageIndex]);

    if (!fields.length) {
        return null;
    }

    return (
        <>
            {fields.map((field) => (
                <StyledFieldHighlightContainer
                    key={field.id}
                    style={{
                        left: field.left,
                        top: field.top,
                        width: field.width,
                        height: field.height,
                    }}
                />
            ))}
        </>
    );
};

const ThumbnailSidebar = ({ documentId }) => {
    const { provides: scroll } = useScroll(documentId);

    return (
        <ThumbnailsPane documentId={documentId}>
            {(meta) => (
                <StyledThumbnailItem
                    key={meta.pageIndex}
                    style={{
                        top: meta.top,
                        height: meta.wrapperHeight,
                    }}
                    onClick={() =>
                        scroll?.scrollToPage({
                            pageNumber: meta.pageIndex + 1,
                        })
                    }
                >
                    <StyledThumbnailImage
                        style={{
                            width: meta.width,
                            height: meta.height,
                        }}
                    >
                        <ThumbImg
                            documentId={documentId}
                            meta={meta}
                        />
                    </StyledThumbnailImage>

                    <StyledThumbnailPageNumber>
                        {meta.pageIndex + 1}
                    </StyledThumbnailPageNumber>
                </StyledThumbnailItem>
            )}
        </ThumbnailsPane>
    );
};

const EmbeddedPdfPreview = ({
    file,
    onFieldCountChange,
}) => {
    const [fileBuffer, setFileBuffer] = useState(null);

    const { engine, isLoading } = usePdfiumEngine();

    useEffect(() => {
        if (!file) {
            setFileBuffer(null);
            return;
        }

        const loadFile = async () => {
            try {
                const buffer = await file.arrayBuffer();

                setFileBuffer(buffer);
            } catch (error) {
                console.error(
                    "Error loading PDF file:",
                    error
                );

                setFileBuffer(null);

                if (onFieldCountChange) {
                    onFieldCountChange(0);
                }
            }
        };

        loadFile();
    }, [file, onFieldCountChange]);

    const plugins = useMemo(() => {
        if (!fileBuffer) {
            return [];
        }

        return [
            createPluginRegistration(
                DocumentManagerPluginPackage,
                {
                    initialDocuments: [
                        {
                            buffer: fileBuffer,
                            name: file?.name || "assessment.pdf",
                        },
                    ],
                }
            ),
            createPluginRegistration(ViewportPluginPackage),
            createPluginRegistration(ScrollPluginPackage),
            createPluginRegistration(RenderPluginPackage),
            createPluginRegistration(
                ThumbnailPluginPackage,
                {
                    width: 100,
                    gap: 10,
                    autoScroll: true,
                }
            ),
            createPluginRegistration(InteractionManagerPluginPackage),
            createPluginRegistration(SelectionPluginPackage),
            createPluginRegistration(HistoryPluginPackage),
            createPluginRegistration(AnnotationPluginPackage),
            createPluginRegistration(FormPluginPackage),
        ];
    }, [fileBuffer, file]);

    if (!file) {
        return null;
    }

    if (isLoading || !engine || !fileBuffer) {
        return (
            <StyledPreviewContainer>
                <div>
                    Loading PDF...
                </div>
            </StyledPreviewContainer>
        );
    }

    return (
        <StyledPreviewContainer>
            <EmbedPDF
                engine={engine}
                plugins={plugins}
            >
                {({ activeDocumentId }) =>
                    activeDocumentId && (
                        <DocumentContent
                            documentId={ activeDocumentId }
                        >
                            {({ isLoaded }) =>
                                isLoaded && (
                                    <>
                                        <StyledThumbnailSidebar>
                                            <ThumbnailSidebar
                                                documentId={ activeDocumentId }
                                            />
                                        </StyledThumbnailSidebar>

                                        <StyledDocumentContainer>
                                            <FormFillMode documentId={ activeDocumentId } />
                                            <Viewport documentId={ activeDocumentId }>
                                                <Scroller
                                                    documentId={ activeDocumentId }
                                                    renderPage={({
                                                        width,
                                                        height,
                                                        pageIndex,
                                                    }) => (
                                                        <div
                                                            style={{
                                                                position: "relative",
                                                                width,
                                                                height,
                                                            }}
                                                        >
                                                            <PagePointerProvider
                                                                documentId={ activeDocumentId }
                                                                pageIndex={ pageIndex }
                                                            >
                                                                <RenderLayer
                                                                    documentId={ activeDocumentId }
                                                                    pageIndex={  pageIndex }
                                                                />
                                                                <SelectionLayer
                                                                    documentId={ activeDocumentId }
                                                                    pageIndex={ pageIndex }
                                                                />
                                                                <AnnotationLayer
                                                                    documentId={ activeDocumentId }
                                                                    pageIndex={ pageIndex }
                                                                />
                                                                <FormFieldHighlightLayer
                                                                    documentId={ activeDocumentId }
                                                                    pageIndex={ pageIndex }
                                                                />
                                                            </PagePointerProvider>
                                                        </div>
                                                    )}
                                                />
                                            </Viewport>
                                        </StyledDocumentContainer>
                                    </>
                                )
                            }
                        </DocumentContent>
                    )
                }
            </EmbedPDF>
        </StyledPreviewContainer>
    );
};

export default EmbeddedPdfPreview;